import React, { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { reduce, findIndex, map, omit, filter, isNil } from 'lodash-es';
import { SimplePopover, LineChart, Typography } from '@dev/ee-components';

import { ChannelChartData } from '@dev/ee-api/ee-api-models';

import { ApplicationState } from '@/store';
import { getPickedCampaign } from '@/store/campaigns/actions';
import { AbSplitLineChartData, ClicksOrOpens } from '@/store/campaigns/types';
import { BarChartColors, LONG_DATE_FORMAT } from '@/services/constants';
import dayjs from '@/services/tools/dayjs';
import { isClickTrackingEnabled, isOpenTrackingEnabled } from '../../Overview/helperFunctions';
import { isNewAbSplit } from '../../../helperFunctions';

const GraphHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
`;

export const AbSplitClicksAndOpensLineChart = (): JSX.Element => {
    const { t } = useTranslation();
    const { isChartDataLoading, campaign, clicksAndOpensChartData, templateLabels } = useSelector(getPickedCampaign);
    const { AdvancedOptions } = useSelector((state: ApplicationState) => state.account);
    const isClicksTracked = isClickTrackingEnabled(campaign, AdvancedOptions);
    const isOpensTracked = isOpenTrackingEnabled(campaign, AdvancedOptions);
    const defaultChartSubject = isOpensTracked ? ClicksOrOpens.Opens : ClicksOrOpens.Clicks;
    const [chartSubject, setChartSubject] = useState<ClicksOrOpens>(defaultChartSubject);

    if (!campaign || !templateLabels || (campaign && !isClicksTracked && !isOpensTracked)) {
        return null;
    }

    const isNewAbSplitCampaign = isNewAbSplit(campaign?.CampaignTemplates);
    let title = isNewAbSplitCampaign ? t('performance') : t('48hperformance');
    if (!isClicksTracked) {
        title += ` ${t('byopens')}`;
    } else if (!isOpensTracked) {
        title += ` ${t('byclicks')}`;
    }

    const isDataLoading = !clicksAndOpensChartData && isChartDataLoading;

    const chartData = isNil(clicksAndOpensChartData)
        ? []
        : GenerateChartData(clicksAndOpensChartData, chartSubject, templateLabels, t);

    const allValuesSum = sumLineChartDataValues(chartData);

    const chartLineConfig = map(Object.keys(templateLabels), (key, index) => ({
        dataKey: `${t('version')} ${templateLabels[key]}`,
        color: BarChartColors[index].bgColor,
    }));

    return (
        <>
            <GraphHeader>
                <Typography variant="h3">{title}</Typography>
                {isClicksTracked && isOpensTracked ? (
                    <SimplePopover
                        value={chartSubject}
                        listItemWidth={'200px'}
                        type={'noborder'}
                        onItemClick={(): void =>
                            setChartSubject(
                                chartSubject === ClicksOrOpens.Opens ? ClicksOrOpens.Clicks : ClicksOrOpens.Opens
                            )
                        }
                        options={[
                            { value: ClicksOrOpens.Opens, label: t('opens') },
                            { value: ClicksOrOpens.Clicks, label: t('clicks') },
                        ]}
                    />
                ) : (
                    ''
                )}
            </GraphHeader>
            <LineChart
                lineConfig={chartLineConfig}
                data={chartData}
                isEmpty={clicksAndOpensChartData && !allValuesSum}
                height={490}
                xAxisGroupName="Date"
                xAxisTickFormatter={xAxisTickFormatter}
                tooltipHeaderFormatter={lineChartTooltipHeaderFormatter}
                showLegend={true}
                isMobile={false}
                padding={{ left: 3, right: 5 }}
                fullWidth={true}
                isLoading={isDataLoading}
                yAllowDecimals={false}
            />
        </>
    );
};

const GenerateChartData = (
    clicksAndOpensChartData: ChannelChartData[],
    chartSubject: ClicksOrOpens,
    templateLabels: Record<string, string>,
    t: TFunction
): AbSplitLineChartData[] => {
    const isNewSplitCampaign = isNewAbSplit(clicksAndOpensChartData);
    const winnerTemplateIndex = findIndex(clicksAndOpensChartData, (el: ChannelChartData) => el.IsWinner);
    const winnerTemplate = { ...clicksAndOpensChartData[winnerTemplateIndex] };
    const dataWithoutWinnerTemplate = filter(clicksAndOpensChartData, (el) => !el.IsWinner);

    return map(winnerTemplate?.Stats, (el, dateIndex): AbSplitLineChartData => {
        const currDateAllTemplatesData = reduce(
            isNewSplitCampaign ? dataWithoutWinnerTemplate : clicksAndOpensChartData,
            (sum, templateData: ChannelChartData) => {
                const key = templateLabels[templateData.ChannelName];
                const value =
                    chartSubject === ClicksOrOpens.Opens
                        ? templateData.Stats[dateIndex].Opens
                        : templateData.Stats[dateIndex].Click;

                return {
                    ...sum,
                    [`${t('version')} ${key}`]: value,
                };
            },
            {}
        );

        return {
            Date: dayjs(el?.Date).format(LONG_DATE_FORMAT),
            ...currDateAllTemplatesData,
        };
    });
};

const xAxisTickFormatter = (tick: string): string => {
    const format = 'LT';
    return dayjs(tick).format(format);
};

const lineChartTooltipHeaderFormatter = (tick: string): string => {
    const format = 'DD-MM LT';
    return dayjs(tick).format(format);
};

const sumLineChartDataValues = (chartData: AbSplitLineChartData[]) =>
    reduce(
        chartData,
        (sum: number, singleChartData: AbSplitLineChartData) =>
            sum +
            reduce(
                omit(singleChartData, ['Date']),
                (singleDateSum: number, singleChartValue: number): number => singleDateSum + singleChartValue,
                0
            ),
        0
    );
